import React, { useState } from 'react';
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle,
  RotateCcw,
  Send,
  Shield,
  User,
  XCircle,
} from 'lucide-react';
import { Badge, Button, Modal } from '../ui';
import type { WorkflowValidationRequest } from '../../services/workflow';
import DirectHandlingFields from './DirectHandlingFields';
import {
  buildDirectResolutionPayload,
  getDirectResolutionVisibilityHint,
  type DirectResolutionErrors,
  validateDirectResolutionFields,
} from '../../utils/workflowResolution';
import { getPlainTextFromHtml, isRichTextMeaningful } from '../../utils/richText';
import { getApprovalNotesPreset } from './structuredNotesPresets';
import { isWarekWorkflowStep } from './workflowStepHelpers';

interface ValidationStep {
  step_order: number;
  step_label: string | null;
  can_resolve_here: boolean;
  validator: { id: number; name: string } | null;
  validator_role?: string | null;
  status: string;
  metadata?: Record<string, any> | null;
}

interface WorkflowTransition {
  to_state: string;
  label: string;
  requires_notes?: boolean;
}

interface LaporanContext {
  id: number;
  kode?: string;
  judul?: string;
  status: string;
  tingkat_risiko?: string;
  unit?: { name: string } | null;
  user?: { name: string } | null;
}

interface Props {
  laporan: LaporanContext;
  currentStep: ValidationStep | null;
  canApprove: boolean;
  canReject: boolean;
  canResolve: boolean;
  transitions: WorkflowTransition[];
  processing: boolean;
  layout?: 'inline' | 'sidebar';
  onApprove?: (payload?: string | WorkflowValidationRequest) => Promise<boolean>;
  onReject?: (feedback: string) => Promise<boolean>;
  onResolve?: (payload: WorkflowValidationRequest) => Promise<boolean>;
  onTransition?: (toState: string, notes?: string) => Promise<boolean>;
}

type ActionType = 'approve' | 'reject' | 'resolve' | 'transition' | null;

interface ActionConfig {
  type: ActionType;
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  variant: 'success' | 'danger' | 'primary' | 'warning' | 'outline';
  requireFeedback: boolean;
  feedbackPlaceholder: string;
  confirmTitle: string;
  confirmMessage: string;
  toState?: string;
}

interface ModalProps {
  action: ActionConfig;
  laporan: LaporanContext;
  currentStep: ValidationStep | null;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  approvalNoteHtml: string;
  onApprovalNoteChange: (value: string) => void;
  approvalNotesPreset: ReturnType<typeof getApprovalNotesPreset>;
  resolutionNoteHtml: string;
  resolutionEvidenceUrl: string;
  resolutionErrors: DirectResolutionErrors;
  onResolutionNoteChange: (value: string) => void;
  onResolutionEvidenceChange: (value: string) => void;
  resolutionVisibilityHint: string;
  onConfirm: () => void;
  onClose: () => void;
  processing: boolean;
}

const UnifiedWorkflowActions: React.FC<Props> = ({
  laporan,
  currentStep,
  canApprove,
  canReject,
  canResolve,
  transitions,
  processing,
  layout = 'inline',
  onApprove,
  onReject,
  onResolve,
  onTransition,
}) => {
  const [selectedAction, setSelectedAction] = useState<ActionConfig | null>(null);
  const [feedback, setFeedback] = useState('');
  const [approvalNoteHtml, setApprovalNoteHtml] = useState('');
  const [resolutionNoteHtml, setResolutionNoteHtml] = useState('');
  const [resolutionEvidenceUrl, setResolutionEvidenceUrl] = useState('');
  const [resolutionErrors, setResolutionErrors] = useState<DirectResolutionErrors>({});
  const [localProcessing, setLocalProcessing] = useState(false);

  const isProcessing = processing || localProcessing;
  const isSidebarLayout = layout === 'sidebar';
  const status = laporan.status;

  const availableActions = buildAvailableActions({
    status,
    currentStep,
    canApprove,
    canReject,
    canResolve,
    transitions,
  });

  if (availableActions.length === 0) {
    return null;
  }

  const resetModalState = () => {
    setFeedback('');
    setApprovalNoteHtml('');
    setResolutionNoteHtml('');
    setResolutionEvidenceUrl('');
    setResolutionErrors({});
  };

  const openModal = (action: ActionConfig) => {
    setSelectedAction(action);
    resetModalState();
  };

  const closeModal = () => {
    setSelectedAction(null);
    resetModalState();
  };

  const handleResolutionNoteChange = (value: string) => {
    setResolutionNoteHtml(value);
    setResolutionErrors((prev) => ({ ...prev, note: undefined }));
  };

  const handleResolutionEvidenceChange = (value: string) => {
    setResolutionEvidenceUrl(value);
    setResolutionErrors((prev) => ({ ...prev, evidenceUrl: undefined }));
  };

  const handleApprovalNoteChange = (value: string) => {
    setApprovalNoteHtml(value);
  };

  const handleConfirm = async () => {
    if (!selectedAction) return;

    if (selectedAction.type === 'resolve') {
      const errors = validateDirectResolutionFields(resolutionNoteHtml, resolutionEvidenceUrl);
      if (Object.keys(errors).length > 0) {
        setResolutionErrors(errors);
        return;
      }
    } else if (selectedAction.requireFeedback && !feedback.trim()) {
      return;
    }

    setLocalProcessing(true);
    let success = false;

    try {
      switch (selectedAction.type) {
        case 'approve':
          if (onApprove) {
            const approvalFeedback = getPlainTextFromHtml(approvalNoteHtml);
            success = await onApprove({
              feedback: approvalFeedback || undefined,
              approval_note_html: isRichTextMeaningful(approvalNoteHtml) ? approvalNoteHtml : undefined,
            });
          }
          break;
        case 'reject':
          if (onReject) {
            success = await onReject(feedback);
          }
          break;
        case 'resolve':
          if (onResolve) {
            success = await onResolve(
              buildDirectResolutionPayload(resolutionNoteHtml, resolutionEvidenceUrl)
            );
          }
          break;
        case 'transition':
          if (onTransition && selectedAction.toState) {
            success = await onTransition(selectedAction.toState, feedback || undefined);
          }
          break;
      }

      if (success) {
        closeModal();
      }
    } finally {
      setLocalProcessing(false);
    }
  };

  const primaryActions = availableActions.filter((action) =>
    ['approve', 'resolve'].includes(action.type || '')
    || (action.type === 'transition' && !action.key.includes('reject') && !action.key.includes('tolak'))
  );

  const secondaryActions = availableActions.filter((action) =>
    ['reject'].includes(action.type || '')
    || (action.type === 'transition' && (action.key.includes('reject') || action.key.includes('tolak')))
  );

  const primaryActionLayoutClass = isSidebarLayout
    ? `grid grid-cols-1 gap-2${primaryActions.length > 1 ? ' sm:grid-cols-2' : ''}`
    : 'flex flex-wrap items-center gap-2';

  const resolveVisibilityHint = (() => {
    return getDirectResolutionVisibilityHint(isWarekWorkflowStep(currentStep) ? 'warek' : 'unit');
  })();

  const approvalNotesPreset = getApprovalNotesPreset(
    isWarekWorkflowStep(currentStep) ? 'to_rapim' : 'to_warek'
  );

  return (
    <>
      <div className="flex flex-col gap-3">
        {primaryActions.length > 0 && (
          <div className={primaryActionLayoutClass}>
            {primaryActions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant}
                size="sm"
                icon={action.icon}
                onClick={() => openModal(action)}
                disabled={isProcessing}
                className={isSidebarLayout ? 'w-full justify-center text-center' : undefined}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {secondaryActions.length > 0 && (
          <div className={isSidebarLayout ? 'grid grid-cols-1 gap-2' : 'flex flex-wrap items-center gap-2'}>
            {secondaryActions.map((action) => (
              <Button
                key={action.key}
                variant="outline"
                size="sm"
                icon={action.icon}
                onClick={() => openModal(action)}
                disabled={isProcessing}
                className={`${isSidebarLayout ? 'w-full justify-center text-center' : ''} text-neutral-text-secondary hover:text-danger hover:border-danger`.trim()}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {currentStep && currentStep.status === 'pending' && (
          <div className={`pt-2 border-t border-neutral-border text-xs text-neutral-text-secondary ${isSidebarLayout ? 'space-y-2' : 'flex flex-wrap items-center gap-2'}`}>
            <span className="inline-flex items-center px-2 py-0.5 bg-teal-main/10 text-teal-main rounded-full font-semibold">
              Tahap {currentStep.step_order}
            </span>
            <span className="font-medium text-neutral-text-primary">{currentStep.step_label}</span>
            {currentStep.validator && (
              <span className="flex items-center gap-1">
                {!isSidebarLayout && <span className="w-1 h-1 rounded-full bg-neutral-border" />}
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {currentStep.validator.name}
                </span>
              </span>
            )}
          </div>
        )}
      </div>

      {selectedAction && (
        <ActionConfirmationModal
          action={selectedAction}
          laporan={laporan}
          currentStep={currentStep}
          feedback={feedback}
          onFeedbackChange={setFeedback}
          approvalNoteHtml={approvalNoteHtml}
          onApprovalNoteChange={handleApprovalNoteChange}
          approvalNotesPreset={approvalNotesPreset}
          resolutionNoteHtml={resolutionNoteHtml}
          resolutionEvidenceUrl={resolutionEvidenceUrl}
          resolutionErrors={resolutionErrors}
          onResolutionNoteChange={handleResolutionNoteChange}
          onResolutionEvidenceChange={handleResolutionEvidenceChange}
          resolutionVisibilityHint={resolveVisibilityHint}
          onConfirm={handleConfirm}
          onClose={closeModal}
          processing={isProcessing}
        />
      )}
    </>
  );
};

function buildAvailableActions(params: {
  status: string;
  currentStep: ValidationStep | null;
  canApprove: boolean;
  canReject: boolean;
  canResolve: boolean;
  transitions: WorkflowTransition[];
}): ActionConfig[] {
  const { status, currentStep, canApprove, canReject, canResolve, transitions } = params;
  const actions: ActionConfig[] = [];
  const normalizedStatus = status === 'draft' ? 'draf' : status;

  if (['draf', 'draft', 'ditolak_l1', 'ditolak_l2'].includes(normalizedStatus)) {
    const submitTransition = transitions.find((transition) => transition.to_state === 'diajukan');
    if (submitTransition) {
      actions.push({
        type: 'transition',
        key: 'submit',
        label: normalizedStatus.includes('ditolak') ? 'Ajukan Ulang' : 'Ajukan Laporan',
        description: 'Kirim laporan untuk divalidasi',
        icon: <Send className="w-4 h-4" />,
        variant: 'primary',
        requireFeedback: false,
        feedbackPlaceholder: '',
        confirmTitle: 'Ajukan Laporan',
        confirmMessage: 'Laporan akan dikirim untuk proses validasi. Pastikan semua data sudah lengkap.',
        toState: 'diajukan',
      });
    }

    return actions;
  }

  if (currentStep && currentStep.status === 'pending') {
    if (canApprove) {
      actions.push({
        type: 'approve',
        key: 'approve',
        label: getApproveLabel(currentStep),
        description: getApproveDescription(currentStep),
        icon: <CheckCircle className="w-4 h-4" />,
        variant: 'success',
        requireFeedback: false,
        feedbackPlaceholder: 'Tambahkan catatan persetujuan (opsional)...',
        confirmTitle: `Setujui: ${currentStep.step_label || 'Validasi'}`,
        confirmMessage: 'Laporan akan disetujui dan dilanjutkan ke tahap berikutnya.',
      });
    }

    if (canResolve && currentStep.can_resolve_here) {
      actions.push({
        type: 'resolve',
        key: 'resolve',
        label: 'Tangani Langsung',
        description: 'Selesaikan masalah tanpa eskalasi ke atas',
        icon: <Shield className="w-4 h-4" />,
        variant: 'primary',
        requireFeedback: true,
        feedbackPlaceholder: 'Jelaskan rencana penanganan dan solusi yang akan diterapkan...',
        confirmTitle: 'Tangani Langsung',
        confirmMessage: 'Masalah akan ditangani langsung di level Anda. Pimpinan terkait akan menerima laporan penanganan.',
      });
    }

    if (canReject) {
      actions.push({
        type: 'reject',
        key: 'reject',
        label: 'Kembalikan untuk Revisi',
        description: 'Minta pelapor memperbaiki data',
        icon: <RotateCcw className="w-4 h-4" />,
        variant: 'outline',
        requireFeedback: true,
        feedbackPlaceholder: 'Jelaskan apa yang perlu diperbaiki oleh pelapor...',
        confirmTitle: 'Kembalikan untuk Revisi',
        confirmMessage: 'Laporan akan dikembalikan ke pelapor untuk diperbaiki.',
      });
    }

    return actions;
  }

  transitions
    .filter((transition) => {
      if (['diajukan', 'validasi_l2', 'ditolak_l1', 'ditolak_l2'].includes(transition.to_state)) {
        return false;
      }
      if (transition.to_state === 'diputuskan') {
        return false;
      }
      return true;
    })
    .forEach((transition) => {
      const config = getTransitionConfig(transition);
      if (config) {
        actions.push(config);
      }
    });

  return actions;
}

function getApproveLabel(step: ValidationStep): string {
  const stepLabel = step.step_label?.toLowerCase() || '';

  if (stepLabel.includes('dekan') || stepLabel.includes('fakultas')) {
    return 'Setujui & Teruskan ke Warek';
  }
  if (stepLabel.includes('warek') || stepLabel.includes('wakil rektor')) {
    return 'Setujui & Agendakan RAPIM';
  }
  if (stepLabel.includes('lembaga')) {
    return 'Setujui & Teruskan';
  }

  return 'Setujui & Lanjutkan';
}

function getApproveDescription(step: ValidationStep): string {
  const stepLabel = step.step_label?.toLowerCase() || '';

  if (stepLabel.includes('dekan')) {
    return 'Laporan akan diteruskan ke Wakil Rektor terkait';
  }
  if (stepLabel.includes('warek')) {
    return 'Laporan akan masuk antrean RAPIM';
  }

  return 'Laporan akan dilanjutkan ke tahap berikutnya';
}

function getTransitionConfig(transition: WorkflowTransition): ActionConfig | null {
  const { to_state, label } = transition;

  switch (to_state) {
    case 'antrean_rapim':
      return {
        type: 'transition',
        key: 'to_rapim',
        label: 'Masukkan ke Antrean RAPIM',
        description: 'Agendakan untuk dibahas di RAPIM',
        icon: <Calendar className="w-4 h-4" />,
        variant: 'primary',
        requireFeedback: false,
        feedbackPlaceholder: '',
        confirmTitle: 'Masukkan ke Antrean RAPIM',
        confirmMessage: 'Laporan akan dimasukkan ke antrean agenda RAPIM.',
        toState: to_state,
      };

    case 'selesai':
      return {
        type: 'transition',
        key: 'complete',
        label: 'Konfirmasi Selesai',
        description: 'Tandai laporan sebagai selesai',
        icon: <CheckCircle className="w-4 h-4" />,
        variant: 'success',
        requireFeedback: false,
        feedbackPlaceholder: 'Catatan penyelesaian (opsional)...',
        confirmTitle: 'Konfirmasi Penyelesaian',
        confirmMessage: 'Tindak lanjut akan ditandai sebagai selesai.',
        toState: to_state,
      };

    case 'diarsipkan':
      return {
        type: 'transition',
        key: 'archive',
        label: 'Arsipkan',
        description: 'Pindahkan ke arsip',
        icon: <Building2 className="w-4 h-4" />,
        variant: 'outline',
        requireFeedback: false,
        feedbackPlaceholder: '',
        confirmTitle: 'Arsipkan Laporan',
        confirmMessage: 'Laporan akan dipindahkan ke arsip.',
        toState: to_state,
      };

    default:
      return {
        type: 'transition',
        key: `to_${to_state}`,
        label: label || to_state,
        description: '',
        icon: <ArrowRight className="w-4 h-4" />,
        variant: 'outline',
        requireFeedback: transition.requires_notes || false,
        feedbackPlaceholder: 'Catatan (opsional)...',
        confirmTitle: label || to_state,
        confirmMessage: `Status akan diubah menjadi "${to_state}".`,
        toState: to_state,
      };
  }
}

const ActionConfirmationModal: React.FC<ModalProps> = ({
  action,
  laporan,
  currentStep,
  feedback,
  onFeedbackChange,
  approvalNoteHtml,
  onApprovalNoteChange,
  approvalNotesPreset,
  resolutionNoteHtml,
  resolutionEvidenceUrl,
  resolutionErrors,
  onResolutionNoteChange,
  onResolutionEvidenceChange,
  resolutionVisibilityHint,
  onConfirm,
  onClose,
  processing,
}) => {
  const variantColors: Record<string, string> = {
    success: 'bg-success/5 border-success/20 text-success',
    danger: 'bg-danger/5 border-danger/20 text-danger',
    primary: 'bg-info/5 border-info/20 text-info',
    warning: 'bg-warning/5 border-warning/20 text-warning',
    outline: 'bg-neutral-bg border-neutral-border text-neutral-text-primary',
  };
  const isResolveAction = action.type === 'resolve';
  const isApproveAction = action.type === 'approve';
  const usesStructuredNotes = isResolveAction || isApproveAction;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={action.confirmTitle}
      size={usesStructuredNotes ? '2xl' : 'md'}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={processing}>
            Batal
          </Button>
          <Button
            variant={action.variant === 'outline' ? 'primary' : action.variant}
            onClick={onConfirm}
            loading={processing}
            disabled={!isResolveAction && action.requireFeedback && !feedback.trim()}
          >
            {action.label}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className={`p-3 rounded-xl border text-sm font-medium flex items-start gap-3 ${variantColors[action.variant]}`}>
          {action.icon}
          <div>
            <p>{action.confirmMessage}</p>
            {action.description && (
              <p className="text-xs opacity-80 mt-1">{action.description}</p>
            )}
          </div>
        </div>

        <div className="p-4 bg-neutral-bg rounded-xl space-y-2">
          <p className="text-xs font-mono text-teal-main">{laporan.kode || '-'}</p>
          <p className="text-sm font-bold text-neutral-text-primary">{laporan.judul || '-'}</p>
          <div className="flex items-center gap-3 flex-wrap">
            {laporan.tingkat_risiko && (
              <Badge variant="risk" risk={laporan.tingkat_risiko as any} />
            )}
            {laporan.unit?.name && (
              <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {laporan.unit.name}
              </span>
            )}
            {laporan.user?.name && (
              <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                <User className="w-3 h-3" /> {laporan.user.name}
              </span>
            )}
          </div>
        </div>

        {currentStep && ['approve', 'reject', 'resolve'].includes(action.type || '') && (
          <div className="p-3 bg-teal-main/5 rounded-xl">
            <p className="text-xs font-semibold text-teal-main">
              Tahap {currentStep.step_order}: {currentStep.step_label}
            </p>
            {action.type === 'approve' && currentStep.can_resolve_here && (
              <p className="text-[10px] text-teal-700 mt-1">
                Tip: Anda juga bisa memilih "Tangani Langsung" jika masalah bisa diselesaikan tanpa eskalasi ke tahap berikutnya.
              </p>
            )}
          </div>
        )}

        {isResolveAction ? (
          <DirectHandlingFields
            noteHtml={resolutionNoteHtml}
            evidenceUrl={resolutionEvidenceUrl}
            onNoteChange={onResolutionNoteChange}
            onEvidenceUrlChange={onResolutionEvidenceChange}
            noteError={resolutionErrors.note}
            evidenceError={resolutionErrors.evidenceUrl}
            visibilityHint={resolutionVisibilityHint}
            disabled={processing}
          />
        ) : isApproveAction ? (
          <DirectHandlingFields
            noteHtml={approvalNoteHtml}
            onNoteChange={onApprovalNoteChange}
            visibilityHint={approvalNotesPreset.accessHint}
            disabled={processing}
            noteLabel={approvalNotesPreset.noteLabel}
            noteRequired={approvalNotesPreset.noteRequired}
            notePlaceholder={approvalNotesPreset.notePlaceholder}
            noteHelperText={approvalNotesPreset.noteHelperText}
            guidanceTitle={approvalNotesPreset.guidanceTitle}
            guidanceItems={approvalNotesPreset.guidanceItems}
            accessTitle={approvalNotesPreset.accessTitle}
            accessHint={approvalNotesPreset.accessHint}
            showEvidenceField={false}
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-neutral-text-primary mb-1.5">
              {action.requireFeedback ? 'Catatan (wajib)' : 'Catatan (opsional)'}
            </label>
            <textarea
              className="w-full rounded-xl border border-neutral-border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-light"
              placeholder={action.feedbackPlaceholder || 'Tambahkan catatan...'}
              value={feedback}
              onChange={(event) => onFeedbackChange(event.target.value)}
              rows={4}
            />
            {action.requireFeedback && !feedback.trim() && (
              <p className="text-xs text-danger flex items-center gap-1 mt-1">
                <XCircle className="w-3 h-3" />
                Catatan wajib diisi untuk melanjutkan
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UnifiedWorkflowActions;
